<template>
  <b-modal
    v-model="visible"
    size="xl"
    scrollable
    hide-header
    modal-class="ai-analysis-modal"
    body-class="p-0"
    @hidden="onHidden"
  >
    <!-- Loading State -->
    <template v-if="loading">
      <div class="text-center py-5">
        <div class="ai-loading-icon mb-3">
          <i class="bx bx-scan"></i>
        </div>
        <p class="text-muted mb-0">Carregando resultado da análise...</p>
      </div>
    </template>

    <!-- Analysis Result -->
    <template v-else-if="analysis">
      <!-- ── Header Hero ── -->
      <div class="ai-hero" :class="`ai-hero--${overallVariant}`">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <div class="ai-hero__label">Análise de Documento com IA</div>
            <h5 class="ai-hero__title mb-0">
              {{ analysis.document_type_detected || 'Documento' }}
            </h5>
          </div>
          <div class="ai-hero__status">
            <div class="ai-hero__status-badge" :class="`ai-hero__status-badge--${overallVariant}`">
              <i :class="overallIcon"></i>
              {{ overallLabel }}
            </div>
          </div>
        </div>
        <p class="ai-hero__summary mb-0 mt-2">{{ analysis.summary }}</p>
        <div class="ai-hero__meta mt-2" v-if="analysis.confidence">
          <div class="ai-confidence">
            <span class="ai-confidence__label">Confiança</span>
            <div class="ai-confidence__bar">
              <div class="ai-confidence__fill" :style="{ width: (analysis.confidence * 100) + '%' }"></div>
            </div>
            <span class="ai-confidence__value">{{ (analysis.confidence * 100).toFixed(0) }}%</span>
          </div>
        </div>
      </div>

      <div class="px-4 pb-4">
        <!-- ── Disclaimer ── -->
        <div class="ai-disclaimer">
          <i class="bx bx-info-circle"></i>
          Os dados abaixo foram extraídos por IA e podem conter imprecisões. Confira os valores antes de aplicar.
        </div>

        <!-- ── Comparison Table ── -->
        <div class="ai-section">
          <div class="ai-section__header">
            <h6 class="mb-0">
              <i class="bx bx-search-alt mr-1"></i>
              Dados Encontrados
            </h6>
            <label v-if="validationChecks.length > 0" class="ai-select-all mb-0">
              <input type="checkbox" v-model="selectAll" @change="toggleAll">
              <span>Selecionar para aplicar</span>
            </label>
          </div>

          <div class="ai-comparison-table">
            <table class="table table-bordered mb-0">
              <thead>
                <tr class="ai-table-head">
                  <th width="36"></th>
                  <th width="100">Campo</th>
                  <th width="100">Status</th>
                  <th>Extraído (IA)</th>
                  <th>Cadastro Atual</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="check in validationChecks"
                  :key="check.field"
                  class="ai-table-row"
                  :class="{
                    'ai-table-row--match': check.status === 'match',
                    'ai-table-row--mismatch': check.status === 'mismatch',
                    'ai-table-row--not-found': check.status === 'not_found',
                    'ai-table-row--selected': selectedFields.includes(check.field)
                  }"
                >
                  <td class="text-center align-middle">
                    <input
                      type="checkbox"
                      v-model="selectedFields"
                      :value="check.field"
                      :disabled="!check.extracted_value"
                    >
                  </td>
                  <td class="align-middle font-weight-bold font-size-12 text-dark">
                    {{ fieldLabels[check.field] || check.field }}
                  </td>
                  <td class="align-middle">
                    <span class="ai-status-tag" :class="`ai-status-tag--${check.status}`">
                      <i :class="statusIcon(check.status)"></i>
                      {{ checkLabel(check.status) }}
                    </span>
                  </td>
                  <td class="align-middle">
                    <span class="font-size-13" :class="{
                      'text-success': check.status === 'match',
                      'text-danger font-weight-bold': check.status === 'mismatch',
                      'text-warning': check.status === 'not_found'
                    }">{{ check.extracted_value || '—' }}</span>
                    <div v-if="check.observation" class="font-size-11 text-muted mt-1">{{ check.observation }}</div>
                  </td>
                  <td class="align-middle font-size-13">
                    {{ check.registered_value || '—' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Flights Section ── -->
        <template v-if="extractedFlights.length > 0">
          <div class="ai-section">
            <div class="ai-section__header">
              <h6 class="mb-0">
                <i class="bx bx-plane-take-off mr-1"></i>
                Voos Extraídos
              </h6>
              <b-form-checkbox
                v-model="applyFlights"
                value="flights"
                class="ai-flight-check"
              >
                Aplicar voos
              </b-form-checkbox>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3" v-for="(flight, idx) in extractedFlights" :key="idx">
                <div class="ai-flight-card" :class="{ 'ai-flight-card--ida': flight.type_flight === 'IDA' || flight.type_flight === 'ida', 'ai-flight-card--volta': flight.type_flight === 'VOLTA' || flight.type_flight === 'volta' }">
                  <div class="ai-flight-card__header">
                    <div class="ai-flight-card__route">
                      <span class="ai-flight-card__iata">{{ flight.origin || '???' }}</span>
                      <i class="bx bx-right-arrow-alt"></i>
                      <span class="ai-flight-card__iata">{{ flight.destination || '???' }}</span>
                    </div>
                    <span class="ai-flight-card__type">{{ flight.type_flight || 'voo' }}</span>
                  </div>
                  <div class="ai-flight-card__body">
                    <div class="ai-flight-card__info" v-if="flight.airline">
                      <i class="bx bx-plane"></i>
                      <span>{{ flight.airline }} <strong v-if="flight.flight_number">{{ flight.flight_number }}</strong></span>
                    </div>
                    <div class="ai-flight-card__info" v-if="flight.boarding_date">
                      <i class="bx bx-log-in-circle"></i>
                      <span>Embarque: <strong>{{ flight.boarding_date }}</strong></span>
                    </div>
                    <div class="ai-flight-card__info" v-if="flight.landing_date">
                      <i class="bx bx-log-out-circle"></i>
                      <span>Pouso: <strong>{{ flight.landing_date }}</strong></span>
                    </div>
                    <div class="ai-flight-card__info" v-if="flight.locator">
                      <i class="bx bx-barcode"></i>
                      <span>Loc: <strong>{{ flight.locator }}</strong></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ── Already Applied Info ── -->
        <div v-if="hasApplied" class="ai-applied-banner">
          <div class="ai-applied-banner__icon">
            <i class="bx bx-check-double"></i>
          </div>
          <div>
            <strong>Dados aplicados</strong> em {{ formatDate(analysis.applied_at) }}
            <div class="font-size-12 mt-1" style="opacity: 0.8">
              Campos: {{ (analysis.applied_fields || []).map(f => fieldLabels[f] || f).join(', ') }}
            </div>
          </div>
        </div>

      </div>
    </template>

    <!-- Empty State -->
    <template v-else>
      <div class="text-center py-5">
        <i class="bx bx-file-blank font-size-24 text-muted d-block mb-2"></i>
        <span class="text-muted">Nenhuma análise encontrada.</span>
      </div>
    </template>

    <!-- ── Footer ── -->
    <template #modal-footer>
      <b-button variant="light" @click="close">Fechar</b-button>
      <b-button
        v-if="analysis && analysis.status === 'completed'"
        variant="primary"
        :disabled="selectedFields.length === 0 && !applyFlights"
        @click="confirmApply"
        class="ai-apply-btn"
      >
        <i class="bx bx-check-double mr-1"></i>
        {{ hasApplied ? 'Reaplicar Selecionados' : 'Aplicar Selecionados' }}
        <span v-if="totalSelected > 0" class="ai-apply-btn__count">{{ totalSelected }}</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Swal from 'sweetalert2'
import { mapActions } from 'vuex'
import { successMessage, errorMessage } from '@/components/alertsMessages'

export default {
  name: 'AnalysisResultModal',
  props: {
    value: Boolean,
    attachmentId: {
      type: Number,
      default: null
    },
    participantId: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      loading: false,
      analysis: null,
      currentParticipant: null,
      selectedFields: [],
      applyFlights: null,
      selectAll: false,
      fieldLabels: {
        name: 'Nome',
        cpf: 'CPF',
        birth_date: 'Data Nasc.',
        rg: 'RG',
        email: 'E-mail',
        phone: 'Telefone',
        passport_number: 'Passaporte',
        nationality: 'Nacionalidade',
        gender: 'Gênero',
        flights: 'Voos'
      }
    }
  },
  computed: {
    visible: {
      get () { return this.value },
      set (v) { this.$emit('input', v) }
    },
    overallStatus () {
      if (!this.analysis || !this.analysis.validation_result) return null
      // Recalcular baseado apenas nos checks encontrados (sem not_found)
      const checks = this.validationChecks
      if (checks.length === 0) return this.analysis.validation_result.overall_status || 'valid'
      const hasMismatch = checks.some(c => c.status === 'mismatch')
      return hasMismatch ? 'invalid' : 'valid'
    },
    overallVariant () {
      const map = { valid: 'success', warnings: 'warning', invalid: 'danger' }
      return map[this.overallStatus] || 'secondary'
    },
    overallLabel () {
      const map = { valid: 'Validado', warnings: 'Alertas', invalid: 'Divergências' }
      return map[this.overallStatus] || 'Pendente'
    },
    overallIcon () {
      const map = {
        valid: 'bx bx-check-circle',
        warnings: 'bx bx-error',
        invalid: 'bx bx-x-circle'
      }
      return map[this.overallStatus] || 'bx bx-help-circle'
    },
    validationChecks () {
      if (!this.analysis || !this.analysis.validation_result) return []
      // Filtrar not_found — só mostrar campos que foram efetivamente extraídos
      return (this.analysis.validation_result.checks || []).filter(c => c.status !== 'not_found')
    },
    extractedFlights () {
      if (!this.analysis || !this.analysis.extracted_data) return []
      return this.analysis.extracted_data.flights || []
    },
    hasApplied () {
      return this.analysis && this.analysis.applied_at
    },
    totalSelected () {
      let count = this.selectedFields.length
      if (this.applyFlights === 'flights') count++
      return count
    }
  },
  watch: {
    value (v) {
      if (v && this.attachmentId) {
        this.loadAnalysis()
      }
    }
  },
  methods: {
    ...mapActions('participant', {
      fetchAnalysis: 'getAttachmentAnalysis',
      applyData: 'applyAnalysisData'
    }),
    async loadAnalysis () {
      this.loading = true
      this.analysis = null
      this.selectedFields = []
      this.applyFlights = null
      this.selectAll = false

      try {
        const res = await this.fetchAnalysis(this.attachmentId)
        this.analysis = res.data
        this.currentParticipant = res.data.current_participant || null
      } catch (err) {
        console.error(err)
        this.analysis = null
      } finally {
        this.loading = false
      }
    },
    statusIcon (status) {
      const map = {
        match: 'bx bx-check',
        mismatch: 'bx bx-x',
        not_found: 'bx bx-minus',
        not_in_registration: 'bx bx-plus'
      }
      return map[status] || 'bx bx-help-circle'
    },
    checkLabel (status) {
      const map = {
        match: 'OK',
        mismatch: 'Diverge',
        not_found: 'Ausente',
        not_in_registration: 'Novo'
      }
      return map[status] || status
    },
    toggleAll () {
      if (this.selectAll) {
        // Selecionar todos os campos que têm valor extraído (exceto os que já conferem)
        this.selectedFields = this.validationChecks
          .filter(c => c.extracted_value)
          .map(c => c.field)
      } else {
        this.selectedFields = []
      }
    },
    formatDate (d) {
      if (!d) return ''
      return this.$moment ? this.$moment(d).format('DD/MM/YYYY HH:mm') : d
    },
    confirmApply () {
      const fields = [...this.selectedFields]
      if (this.applyFlights === 'flights') {
        fields.push('flights')
      }
      if (fields.length === 0) return

      Swal.fire({
        title: 'Aplicar dados extraídos?',
        html: `<p style="font-size:13px;color:#74788d">Os seguintes campos serão atualizados:</p><ul style="list-style:none;padding:0;margin:0">${fields.map(f => `<li style="padding:4px 0;font-size:13px"><i class="bx bx-check" style="color:#34c38f;margin-right:4px"></i>${this.fieldLabels[f] || f}</li>`).join('')}</ul>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#556ee6',
        cancelButtonColor: '#74788d',
        cancelButtonText: 'Cancelar',
        confirmButtonText: '<i class="bx bx-check-double"></i> Aplicar'
      }).then(async (result) => {
        if (result.value) {
          try {
            await this.applyData({ id: this.attachmentId, fields })
            successMessage('Dados aplicados com sucesso!', 2500)
            this.loadAnalysis()
            this.$emit('applied')
            this.$root.$emit('participant:reload', this.participantId)
          } catch (err) {
            errorMessage('Erro ao aplicar dados: ' + (err.response?.data?.message || err.message))
          }
        }
      })
    },
    close () {
      this.visible = false
    },
    onHidden () {
      if (this.analysis && this.analysis.applied_at) {
        this.$root.$emit('participant:reload', this.participantId)
      }
      this.analysis = null
      this.selectedFields = []
      this.applyFlights = null
    }
  }
}
</script>

<style scoped>
/* ── Loading Animation ── */
.ai-loading-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(85, 110, 230, 0.1);
  color: #556ee6;
  font-size: 24px;
  animation: ai-scan 1.5s ease-in-out infinite;
}
@keyframes ai-scan {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.7; }
}

/* ── Hero Header ── */
.ai-hero {
  padding: 20px 24px;
  border-bottom: 1px solid #eff2f7;
}
.ai-hero--success { background: linear-gradient(135deg, rgba(52,195,143,0.06) 0%, rgba(52,195,143,0.02) 100%); }
.ai-hero--warning { background: linear-gradient(135deg, rgba(241,180,76,0.06) 0%, rgba(241,180,76,0.02) 100%); }
.ai-hero--danger { background: linear-gradient(135deg, rgba(244,106,106,0.06) 0%, rgba(244,106,106,0.02) 100%); }
.ai-hero--secondary { background: #fafbfc; }

.ai-hero__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #74788d;
  font-weight: 600;
  margin-bottom: 4px;
}
.ai-hero__title {
  font-size: 18px;
  font-weight: 600;
  color: #343a40;
}
.ai-hero__summary {
  font-size: 13px;
  color: #74788d;
  line-height: 1.5;
}

/* ── Status Badge ── */
.ai-hero__status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}
.ai-hero__status-badge--success { background: rgba(52,195,143,0.12); color: #34c38f; }
.ai-hero__status-badge--warning { background: rgba(241,180,76,0.12); color: #c48d1a; }
.ai-hero__status-badge--danger { background: rgba(244,106,106,0.12); color: #f46a6a; }
.ai-hero__status-badge--secondary { background: rgba(116,120,141,0.12); color: #74788d; }

/* ── Confidence Bar ── */
.ai-confidence {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ai-confidence__label {
  font-size: 11px;
  color: #74788d;
  white-space: nowrap;
}
.ai-confidence__bar {
  flex: 1;
  max-width: 120px;
  height: 4px;
  border-radius: 2px;
  background: rgba(0,0,0,0.06);
  overflow: hidden;
}
.ai-confidence__fill {
  height: 100%;
  border-radius: 2px;
  background: #556ee6;
  transition: width 0.6s ease;
}
.ai-confidence__value {
  font-size: 12px;
  font-weight: 600;
  color: #343a40;
  min-width: 32px;
}

/* ── Disclaimer ── */
.ai-disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 6px;
  background: #fafbfc;
  border: 1px solid #eff2f7;
  font-size: 12px;
  color: #74788d;
  margin: 16px 0;
}
.ai-disclaimer i {
  font-size: 16px;
  color: #556ee6;
  flex-shrink: 0;
  margin-top: 1px;
}

/* ── Section ── */
.ai-section {
  margin-bottom: 20px;
}
.ai-section__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.ai-section__header h6 {
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
}
.ai-select-all {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #74788d;
  cursor: pointer;
}

/* ── Comparison Table ── */
.ai-comparison-table {
  border: 1px solid #eff2f7;
  border-radius: 6px;
  overflow: hidden;
}
.ai-comparison-table .table {
  margin-bottom: 0;
}
.ai-comparison-table .table th,
.ai-comparison-table .table td {
  padding: 8px 10px;
  vertical-align: middle;
  border-color: #eff2f7;
}
.ai-table-head th {
  background: #fafbfc;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #74788d;
  border-bottom-width: 1px !important;
  white-space: nowrap;
}
.ai-table-row { transition: background 0.15s ease; }
.ai-table-row:hover { background: #fafbfc; }
.ai-table-row--selected { background: rgba(85,110,230,0.04) !important; }
.ai-table-row--mismatch { border-left: 3px solid #f46a6a; }

/* ── Status Tags ── */
.ai-status-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.ai-status-tag i { font-size: 12px; }
.ai-status-tag--match { background: rgba(52,195,143,0.1); color: #34c38f; }
.ai-status-tag--mismatch { background: rgba(244,106,106,0.1); color: #f46a6a; }
.ai-status-tag--not_found { background: rgba(241,180,76,0.1); color: #c48d1a; }
.ai-status-tag--not_in_registration { background: rgba(85,110,230,0.1); color: #556ee6; }

/* ── Flight Cards ── */
.ai-flight-card {
  border: 1px solid #eff2f7;
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.15s ease;
}
.ai-flight-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }

.ai-flight-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  background: #fafbfc;
  border-bottom: 1px solid #eff2f7;
}
.ai-flight-card--ida .ai-flight-card__header { border-top: 3px solid #556ee6; }
.ai-flight-card--volta .ai-flight-card__header { border-top: 3px solid #f1b44c; }

.ai-flight-card__route {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #343a40;
}
.ai-flight-card__route i { color: #adb5bd; font-size: 18px; }
.ai-flight-card__iata {
  background: #fff;
  border: 1px solid #e2e5e8;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.ai-flight-card__type {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(85,110,230,0.1);
  color: #556ee6;
}
.ai-flight-card--volta .ai-flight-card__type {
  background: rgba(241,180,76,0.1);
  color: #c48d1a;
}
.ai-flight-card__body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ai-flight-card__info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #74788d;
}
.ai-flight-card__info i {
  font-size: 14px;
  color: #adb5bd;
  width: 16px;
  text-align: center;
}
.ai-flight-card__info strong { color: #343a40; }

.ai-flight-check {
  font-size: 12px;
}

/* ── Applied Banner ── */
.ai-applied-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(85,110,230,0.06) 0%, rgba(85,110,230,0.02) 100%);
  border: 1px solid rgba(85,110,230,0.15);
  color: #556ee6;
}
.ai-applied-banner__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(85,110,230,0.12);
  font-size: 20px;
  flex-shrink: 0;
}

/* ── Apply Button ── */
.ai-apply-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ai-apply-btn__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: rgba(255,255,255,0.25);
  font-size: 11px;
  font-weight: 700;
}
</style>

<style>
/* ── Custom Scrollbar (non-scoped to reach .modal-body) ── */
.ai-analysis-modal .modal-dialog .modal-content .modal-body {
  scrollbar-width: thin !important;
  scrollbar-color: transparent transparent !important;
}
.ai-analysis-modal .modal-dialog .modal-content .modal-body:hover {
  scrollbar-color: #d0d5dd transparent !important;
}
.ai-analysis-modal .modal-dialog .modal-content .modal-body::-webkit-scrollbar { width: 5px !important; height: 5px !important; }
.ai-analysis-modal .modal-dialog .modal-content .modal-body::-webkit-scrollbar-track { background: transparent !important; }
.ai-analysis-modal .modal-dialog .modal-content .modal-body::-webkit-scrollbar-thumb { background-color: transparent !important; border-radius: 3px !important; }
.ai-analysis-modal .modal-dialog .modal-content .modal-body:hover::-webkit-scrollbar-thumb { background-color: #d0d5dd !important; }
.ai-analysis-modal .modal-dialog .modal-content .modal-body::-webkit-scrollbar-thumb:hover { background-color: #b0b5bd !important; }
</style>
